<template>
  <div class="checkout-container-placeholder">
    <oie-expandable-item :class="[{ 'expandable-item--is-disabled': !isActiveSection }]" :heading="t('campaignSectionTitle')" :open-on-init="cart.promoCode || (cart.giftCards && cart.giftCards.length > 0)">
      <div class="form-container">
        <div class="form-container__top">
          <h3>{{ t('campaignCodeTitle') }}</h3>

          <!-- we could show this incase there is a campaign code applied, so a v-if on the <p> -->
          <p>
            {{ t('campaignCodeActiveDescription') }}
          </p>
        </div>

        <template v-if="!cart.promoCode || !campaign.campaignDescription">
          <oie-base-input
            id="campaign-code"
            :value="campaignCode"
            :label-text="t('campaignCodeLabel')"
            :placeholder="t('campaignCodePlaceholder')"
            css-classes="checkout-actions__input"
            @input="($event) => $emit('update:campaignCode', $event)"></oie-base-input>

          <oie-base-button css-classes="button--with-border button--blue" :text="t('useCampaignCodeAction')" @click="activateCampaignCode()"></oie-base-button>
        </template>

        <template v-else>
          <oie-message-box class="message-box--green message-box--compact">
            <svg
              width="15"
              height="15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"><path d="M5.52 3.865a1.656 1.656 0 1 1-3.312 0 1.656 1.656 0 0 1 3.312 0Zm-1.104 0a.552.552 0 1 0-1.104 0 .552.552 0 0 0 1.104 0Z" fill="currentColor" /><path d="M1.104 0h5.063c.293 0 .574.116.781.324l7.729 7.728a1.104 1.104 0 0 1 0 1.561l-5.064 5.064a1.104 1.104 0 0 1-1.56 0L.322 6.948A1.104 1.104 0 0 1 0 6.168V1.103A1.104 1.104 0 0 1 1.104 0Zm0 6.167 7.729 7.73 5.063-5.064-7.729-7.729H1.104v5.063Z" fill="#333" /></svg>

            {{ campaign.promoDescription }}

            <oie-action-link @click="removeCampaignCode()">
              <oie-use-svg svg="close" class="message-bar__x-icon"></oie-use-svg>
            </oie-action-link>
          </oie-message-box>

          <ul class="c-list c-list--dimmed c-list--bullet">
            <li v-for="item of campaign.listItems" :key="item">
              {{ item }}
            </li>
          </ul>
        </template>

        <!-- gift card -->
        <template>
          <validation-observer
            ref="giftCardForm"
            tag="form"
            class="form-container"
            @submit.prevent
            @keyup.enter="activateGiftCard()">
            <validation-provider
              v-slot="{errors}"
              vid="giftCardCode"
              slim>
              <oie-base-input
                id="gift-card-code"
                :value="giftCardCode"
                :max-length="19"
                :label-text="t('giftCardCodeLabel')"
                :placeholder="t('giftCardCodePlaceholder')"
                :error-messages="errors"
                data-pw="gift-card-code"
                css-classes="checkout-actions__input"

                @input="($event) => $emit('update:giftCardCode', $event )"></oie-base-input>
            </validation-provider>

            <oie-base-button
              v-element-loader="{ endpoint: 'checkout/validateGiftCardCode' }"
              css-classes="button--with-border button--blue"
              :text="t('useGiftCardCodeAction')"
              @click="activateGiftCard()"></oie-base-button>
          </validation-observer>
        </template>

        <template>
          <ul class="c-list c-list--flex c-list--flex-child">
            <li v-for="item of cart.giftCards" :key="item.giftCardID">
              <span>{{ item.code }}: <strong>{{ formatPrice(item.balance) }}</strong></span>
              <oie-base-action theme="destructive-flat" :aria-label="tStatic('removeGiftCardLabel')" @click="removeGiftCard(item.giftCardID)">
                <oie-base-icon icon="close" size="s"></oie-base-icon>
              </oie-base-action>
            </li>
          </ul>
        </template>
      </div>
    </oie-expandable-item>
    <oie-expandable-item
      v-element-loader="{ endpoint: 'checkout/getPaymentMethods' }"
      :component-index="componentIndex"
      :heading="t('paymentMethodsLabel')"
      :class="['expandable-item--seperate-items', { 'expandable-item--is-disabled': !isActiveSection }]">
      <template
        v-if="paymentMethodInfo && availablePaymentMethods"
        slot="pocket">
        <oie-message-box
          class="message-box--compact"
          color="orange"
          v-html="paymentMethodInfo">
        </oie-message-box>
      </template>
      <template v-for="paymentType in availablePaymentMethods">
        <oie-base-radio
          v-if="(paymentType.systemKeyword !== 'Offer' && !isOrderRequestVisitorState) || paymentType.systemKeyword === 'Offer'"
          :id="paymentType.paymentMethodId"
          :key="`payment-type-${paymentType.paymentMethodId}`"
          v-model="selectedCardPayment"
          name="payment"
          :value="paymentType.systemKeyword"
          :is-checked="checkoutState.selectedCardPayment === paymentType.systemKeyword"
          :label-text="paymentType.description">
          <oie-base-image
            :images="
              getAssetPath(
                `/globalassets/payment-images/${paymentType.systemKeyword}_${clientModel.currentMarket}.png?preset=ProductThumb`
              )
            "
            :alt-text="paymentType.description"
            image-width="3.75rem"
            image-height="3.75rem"
            class="checkout-section__item-logo"></oie-base-image>
        </oie-base-radio>
      </template>

      <div v-if="selectedCardPayment && !(selectedCardPayment === 'Invoice' || selectedCardPayment === 'InvoiceRequest' || selectedCardPayment === 'Offer')" class="form-container">
        <div class="pdf-receipt-information">
          <div class="pdf-receipt-information__icon">
            <oie-use-svg svg="information"></oie-use-svg>
          </div>
          <div class="pdf-receipt-information__content">
            {{ pdfReceiptInformationText }}
          </div>
        </div>

        <oie-base-checkbox
          v-model="customer.pdfReceipt"
          size="large"
          is-for="checkout-pdf-invoice"
          text="PDF Kvitto"
          @input="saveCheckoutPayload()"></oie-base-checkbox>

        <validation-observer
          v-if="customer.pdfReceipt"
          ref="pdfReceiptForm"
          tag="div"
          class="pdfReceiptForm">
          <validation-provider v-slot="{ errors }" rules="required|email:true" slim>
            <oie-base-input
              v-model="customer.pdfReceiptEmail"
              :textarea="false"
              :label-text="`${tStatic('pdfEmailReceiptLabel')}*`"
              :placeholder="tStatic('pdfEmailReceiptPlaceholder')"
              :max-length="100"
              :error-messages="errors"
              css-classes="checkout-actions__input"
              event-key="checkout-container-customer-receipt-email"
              @blur="saveCheckoutPayload()"></oie-base-input>
          </validation-provider>
        </validation-observer>
      </div>

      <div v-if="useInvoice" class="form-container">
        <template v-if="(selectedCardPayment === 'InvoiceRequest' && customer) || (selectedCardPayment === 'Invoice' && superUserShowInvoice && !customer.hasInvoice)">
          <validation-observer
            ref="idNumberForm"
            tag="div"
            class="idNumberForm">
            <validation-provider v-slot="{ errors, touched, dirty }" :rules="createIdNumberFilledOutRule" slim>
              <oie-base-input
                id="customer-id-number"
                :key="idNumberTriggerUpdateKey"
                v-model="customer.idNumber"
                :is-readonly="isIdNumberReadonly"
                :mask="getMask"
                :textarea="false"
                :label-text="`${idNumberLabel}*`"
                :placeholder="idNumberPlaceholder"
                :error-messages="touched && dirty ? errors : []"
                class="checkout-actions__input checkout-actions__input-orgnumber"
                event-key="checkout-container-customer-id-number"></oie-base-input>
            </validation-provider>
          </validation-observer>

          <oie-expandable-information
            :title="tStatic('checkoutInvoiceRequirementTitleLabel')"
            :content="tStatic('checkoutInvoiceRequirementText')"></oie-expandable-information>
        </template>

        <div v-if="pdfInvoiceEmailChangeDisabled" class="checkout-actions__pdf-invoice-email-change-disabled">
          <oie-use-svg svg="circle-error-light"></oie-use-svg><span>{{ tStatic('pdfInvoiceEmailChangeDisabled') }}</span>
        </div>

        <oie-base-checkbox
          v-if="!pdfInvoiceEmailChangeDisabled && (selectedCardPayment === 'Invoice' || selectedCardPayment === 'InvoiceRequest')"
          v-model="customer.pdfInvoice"
          size="large"
          is-for="checkout-pdf-invoice"
          :text="tStatic('pdfInvoiceLabel')"
          @input="saveCheckoutPayload()"></oie-base-checkbox>

        <validation-observer
          v-if="
            (selectedCardPayment === 'Invoice' ||
              selectedCardPayment === 'InvoiceRequest') &&
              customer.pdfInvoice"
          ref="pdfInvoiceForm"
          tag="div"
          class="pdfInvoiceForm">
          <validation-provider v-slot="{ errors }" rules="required|email:true" slim>
            <oie-base-input
              id="customer-pdf-invoice-email"
              v-model="customer.pdfInvoiceEmail"
              :textarea="false"
              :label-text="`${tStatic('pdfEmailInvoiceLabel')}*`"
              :placeholder="tStatic('pdfEmailInvoicePlaceholder')"
              :max-length="100"
              :error-messages="errors"
              :is-readonly="isPdfEmailReadonly"
              css-classes="checkout-actions__input"
              event-key="checkout-container-customer-invoice-email"
              @blur="saveCheckoutPayload()"></oie-base-input>
          </validation-provider>
        </validation-observer>
      </div>
    </oie-expandable-item>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import accordionMixin from '@/mixins/accordion.js'
import nextStepMixin from '@/mixins/checkout-next-step.js'
import gtm from '@/core/enhanced-ecommerce-gtm'
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { CHECKOUT_RESET_PAYMENT, CHECKOUT_SIGN_UP, CHECKOUT_RELOAD_SHIPPING, CHECKOUT_PAYMENT } from '@/variables/checkout-event-keys'

export default {
  mixins: [accordionMixin, nextStepMixin],
  props: {
    eventKey: {
      type: String,
      default: null
    },
    customer: {
      type: Object,
      default: null
    },
    enableInvoiceRequestAnonymousUser: {
      type: Boolean,
      default: false
    },
    campaignCode: {
      type: String,
      default: ''
    },
    giftCardCode: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    paymentMethods: [],
    paymentMethodInfo: null,
    selectedCardPayment: null,
    isSelectedResetFromEvent: false,
    idNumberTriggerUpdateKey: 0,
    toggleUserPassword: false
  }),
  computed: {
    ...mapGetters('checkout', ['isOrderRequestVisitorState', 'isStep3Active']),
    ...mapGetters('commerce', ['cart', 'campaign']),
    ...mapGetters(['enableGtm']),
    availablePaymentMethods() {
      var items = this.paymentMethods
      items = this.removeInvoiceRequestForNonCompanyCustomers(items)
      if (
        (this.customer == null ||
          this.customer === undefined ||
          this.customer.anonymous) &&
        !this.enableInvoiceRequestAnonymousUser
      ) {
        items = Object.keys(items)
          .map((key) => items[key])
          .filter(
            (item) =>
              item.systemKeyword !== 'InvoiceRequest' &&
              item.systemKeyword !== 'Invoice' &&
              (item.systemKeyword !== 'Advance' || this.isSuperUser)
          )
      } else {
        if ((this.customer && this.customer.invoicedBlocked)) {
          var filterInvoicedBlocked = Object.keys(items)
            .map((key) => items[key])
            .filter(
              (item) =>
                item.systemKeyword !== 'InvoiceRequest' &&
                item.systemKeyword !== 'Invoice' &&
                (item.systemKeyword !== 'Advance'))
          items = filterInvoicedBlocked
        } else if ((this.customer && this.customer.hasInvoice && !this.customer.invoicedBlocked) || this.superUserShowInvoice) {
          var filterRequest = Object.keys(items)
            .map((key) => items[key])
            .filter((item) => item.systemKeyword !== 'InvoiceRequest' && (item.systemKeyword !== 'Advance' || this.isSuperUser))
          items = filterRequest
        } else {
          var filterInvoice = Object.keys(items)
            .map((key) => items[key])
            .filter((item) => item.systemKeyword !== 'Invoice' && (item.systemKeyword !== 'Advance' || this.isSuperUser))
          items = filterInvoice
        }
      }
      return items
    },
    createIdNumberFilledOutRule() {
      if (
        this.checkoutState.deliveryAddress.vatNumber &&
        this.checkoutState.deliveryAddress.isCompanyCustomer
      ) {
        return `required|max:20`
      } else if (this.isSuperUser && this.clientModel.currentMarket === 'no') {
        return `required|max:20`
      }
      return `required|min:10|max:20`
    },
    idNumberLabel() {
      if (this.checkoutState.deliveryAddress.isCompanyCustomer &&
          this.clientModel.currentMarket !== 'no' &&
          this.clientModel.currentMarket !== 'se') {
        return this.tStatic('vatNumberLabel')
      }
      return this.tStatic('idNumberLabel')
    },
    idNumberPlaceholder() {
      if (this.checkoutState.deliveryAddress.isCompanyCustomer &&
          this.clientModel.currentMarket !== 'no' &&
          this.clientModel.currentMarket !== 'se') {
        return this.tStatic('VATNumberPlaceholderText')
      }
      return this.tStatic('idNumberPlaceholderText')
    },
    isIdNumberReadonly() {
      var isCustomer = false
      var hasVatNumber = false
      var hasVatNumberLength = false
      var isCompany = false

      if (this.customer) {
        isCustomer = true
      }
      if (this.customer.vatNumber) {
        hasVatNumber = true
      }
      if (hasVatNumber && this.customer.vatNumber.length > 0) {
        hasVatNumberLength = true
      }
      if (this.customer.isCompany) {
        isCompany = true
      }

      return (
        this.checkoutState.deliveryAddress &&
        this.checkoutState.deliveryAddress.vatNumber &&
        this.checkoutState.deliveryAddress.isCompanyCustomer) ||
      (isCustomer &&
        hasVatNumber &&
        hasVatNumberLength &&
        isCompany && !this.isSuperUser)
    },
    getMask() {
      if (this.clientModel.currentMarket === 'se') {
        return {
          mask: '######I#I####',
          tokens: {
            'I': {
              pattern: /[\d -]/
            },
            '#': {
              pattern: /\d/
            }
          }
        }
      }
      return ''
    },
    isSuperUser () {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    superUserShowInvoice () {
      return (this.isSuperUser && this.customer) || (this.isSuperUser && this.toggleUserPassword && this.customer.anonymous)
    },
    useInvoice() {
      return ((this.clientModel.marketUseVatCode && this.selectedCardPayment === 'InvoiceRequest' && this.customer) ||
      this.selectedCardPayment === 'Invoice' ||
      this.selectedCardPayment === 'InvoiceRequest' ||
      ((this.selectedCardPayment === 'Invoice' ||
      this.selectedCardPayment === 'InvoiceRequest') && this.customer.pdfInvoice))
    },
    isPdfEmailReadonly() {
      return this.selectedCardPayment !== 'InvoiceRequest' && !this.isSuperUser && this.customer.hasUsedPdfInvoice
    },
    selectedCountryCode() {
      return this.checkoutState.deliveryAddress.countryCode
    },
    pdfInvoiceEmailChangeDisabled() {
      return this.customer.gediProfile && this.customer.gediProfile.startsWith('ADV-')
    },
    pdfReceiptInformationText() {
      var paymentMethod = this.availablePaymentMethods.find(pm => pm.systemKeyword === this.selectedCardPayment)
      return this.tStatic('checkoutPdfRecieptInfoText').replace('{paymentmethod}', paymentMethod ? paymentMethod.description : '')
    }
  },
  watch: {
    selectedCardPayment(value) {
      this.publish(this.eventKey, value)

      if (!this.isStep3Active && this.enableGtm) {
        return
      }

      if (!this.isSelectedResetFromEvent) {
        this.publish(
          `checkout-wizard-step-${this.componentIndex}`,
          this.componentIndex
        )

        if (this.enableGtm) {
          gtm.addCheckoutStep('add_payment_info', this.cart, { payment_type: value })
        }
      }

      this.isSelectedResetFromEvent = false
    },
    enableInvoiceRequestAnonymousUser(value) {
      console.warn(`Setting idNumber to vatNumber in enableInvoiceRequestAnonymousUser. Changed "${this.customer.idNumber}" to "${this.checkoutState.deliveryAddress.vatNumber}"`)
      this.customer.idNumber = this.checkoutState.deliveryAddress.vatNumber
      this.resetSelectedPayment()
    },
    customer(value) {
      this.resetSelectedPayment()
    },
    isOrderRequestVisitorState() {
      this.resetSelectedPayment()
    },
    selectedCountryCode(value) {
      this.loadPaymentMethods()
    }
  },
  mounted() {
    this.loadPaymentMethods()

    this.subscribe(CHECKOUT_RESET_PAYMENT, () => {
      this.clearSelectedPaymentStep()
    })

    this.subscribe(CHECKOUT_SIGN_UP, (value) => {
      if (value.vatNumber) {
        console.warn(`Setting idNumber to vatNumber in  this.subscribe(CHECKOUT_SIGN_UP. Changed "${this.customer.idNumber}" to "${value.vatNumber}"`)
        this.customer.idNumber = value.vatNumber
        this.idNumberTriggerUpdateKey++
        this.publish(
          'checkout-container-customer-id-number',
          this.customer.idNumber
        )
      }
      this.toggleUserPassword = value.toggleUserPassword
    })

    this.subscribe('PUSH_MANUAL_SELECTED_PAYMENT', () => {
      if (this.isStep3Active && this.customer.hasInvoice && !this.customer.invoicedBlocked) {
        this.publish(`checkout-wizard-step-${this.componentIndex}`, this.componentIndex)
      }
    })

    console.log(this.customer)
  },
  methods: {
    ...mapActions('commerce', ['removeGiftCardCode', 'applyCampaign', 'getCart']),
    removeInvoiceRequestForNonCompanyCustomers(items) {
      if (
        (this.checkoutState.deliveryAddress && this.checkoutState.deliveryAddress.countryCode === 'SE') ||
        (this.checkoutState.deliveryAddress && this.checkoutState.deliveryAddress.isCompanyCustomer &&
          this.checkoutState.deliveryAddress.vatNumber) ||
          (this.customer && this.customer.isCompany && this.customer.vatNumber)
      ) {
        return items
      }
      return Object.keys(items)
        .map((key) => items[key])
        .filter((item) => item.systemKeyword !== 'InvoiceRequest')
    },
    resetSelectedPayment() {
      const previousPayment = this.checkoutState.selectedCardPayment
      this.selectedCardPayment = null
      if (previousPayment) {
        // If payment methods haven't loaded yet, don't validate - loadPaymentMethods()
        // will call resetSelectedPayment() again once the data is available.
        if (this.paymentMethods.length === 0) {
          return
        }

        const isStillAvailable = _.findIndex(this.availablePaymentMethods, (o) => {
          // Mirror the v-if filter used on the radio buttons: when isOrderRequestVisitorState
          // is true only 'Offer' is rendered; otherwise only non-'Offer' methods are rendered.
          const isVisible = (o.systemKeyword !== 'Offer' && !this.isOrderRequestVisitorState) || o.systemKeyword === 'Offer'
          return o.systemKeyword === previousPayment && isVisible
        }) > -1

        if (isStillAvailable) {
          this.selectedCardPayment = previousPayment
        } else {
          // Payment is no longer available - clear the store immediately instead of
          // relying on the async Vue watcher to propagate the null value.
          this.isSelectedResetFromEvent = true
          this.publish(this.eventKey, null)
        }
      }
      if (this.customer && this.customer.vatNumber) {
        console.warn(`Setting idNumber to vatNumber in resetSelectedPayment. Changed "${this.customer.idNumber}" to "${this.customer.vatNumber}"`)
        this.customer.idNumber = this.customer.vatNumber
        this.publish(
          'checkout-container-customer-id-number',
          this.customer.idNumber
        )
      }
    },
    clearSelectedPaymentStep() {
      this.isSelectedResetFromEvent = true
      this.selectedCardPayment = null
      this.isOpen = false
    },
    saveCheckoutPayload() {
      this.publish('checkout-pdf-invoice-receipt',
        this.customer.pdfInvoice,
        this.customer.pdfInvoiceEmail,
        this.customer.pdfReceipt,
        this.customer.pdfReceiptEmail
      )
    },
    loadPaymentMethods() {
      const isCompany = !!this.customer.isCompany || !!this.checkoutState.deliveryAddress.isCompanyCustomer
      httpService.get('checkout/getPaymentMethods', { countryCode: this.selectedCountryCode, isCompany }).then((result) => {
        this.paymentMethods = result.data.data.methods
        this.paymentMethodInfo = result.data.data.message
        this.resetSelectedPayment()
      })
    },
    async removeCampaignCode() {
      this.$emit('update:campaignCode', null)
      this.cart.promoCode = null
      await this.applyCampaign({ promoCode: '' })
      await this.publish(CHECKOUT_RELOAD_SHIPPING)
      await this.getCart(1)
    },
    async activateCampaignCode() {
      await this.applyCampaign({ promoCode: this.campaignCode })
      await this.publish(CHECKOUT_RELOAD_SHIPPING)
      await this.getCart(1)
    },
    async activateGiftCard() {
      try {
        await httpService.post('checkout/activateGiftCardCode', { code: this.giftCardCode })
        this.$emit('update:giftCardCode', null)
        this.loadPaymentMethods()
        await this.publish(CHECKOUT_PAYMENT)
      } catch (error) {
        if (error.response && error.response.status === 400) {
          this.$refs.giftCardForm.setErrors(error.response.data.errors)
        }
      }
    },
    async removeGiftCard(giftCardId) {
      await this.removeGiftCardCode({ giftCardId: giftCardId })
      this.$emit('update:giftCardCode', null)
      this.loadPaymentMethods()
      this.publish(CHECKOUT_PAYMENT)
    }
  }
}
</script>

<style lang="scss" scoped>
.checkout-actions__input-orgnumber {
  margin-bottom: measure(quarter);
}

  .checkout-actions__pdf-invoice-email-change-disabled {
    display: flex;
    align-items: center;
  }

  .checkout-actions__pdf-invoice-email-change-disabled > span {
    margin-left: 0.5rem;
  }

.pdf-receipt-information {
  max-width: 65ch;
  display: flex;
  align-items: flex-start;
}

.pdf-receipt-information__icon {
  flex-shrink: 0;
  margin-right: measure(quarter);
}

.pdf-receipt-information__icon svg {
  width: 1.3rem;
  height: 1.3rem;
  color: color(yellow-1);
  margin-top: 0.20rem;
}

.pdf-receipt-information__content {
  flex: 1;
}
</style>
